import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
    getPasses,
    createPass,
    deletePass,
    updatePass,
    addPassToInvoice,
} from "../../api/pass";
import {
    createPassPurchase,
    getPassPurchasesByUser,
    getPassPurchases,
} from "../../api/passPurchase";
import { searchContestants } from "../../api/contestant";
import {
    getInvoicesByContestant,
    getAllPassesFromInvoice,
    removePassFromInvoice,
} from "../../api/invoice";
import {
    Button,
    Card,
    Space,
    notification,
    Typography,
    Flex,
    List,
    Modal,
    Input,
    Tabs,
    Skeleton,
    Pagination,
    Tag,
    message,
} from "antd";
import { PlusOutlined, CloseOutlined } from "@ant-design/icons";
import PassTable from "../../components/Passes/passTable";
import PassForm from "../../components/Passes/passForm";
import CreatePassForm from "../../components/Passes/createPassForm";
import { openNotificationWithIcon } from "../../utils/notificationUtils";
import { useSettings } from "../../contexts/settingsContext";
import { loadState } from "../../stores/store";

const { Title } = Typography;
const { TabPane } = Tabs;

const Passes = () => {
    const { state: settings } = useSettings();

    const role = useMemo(() => loadState().user.role, []); // Memoized role to prevent unnecessary re-computations  
    const [userRole] = useState(role); // Initialize userRole once since it doesn't change during component lifecycle  

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [passes, setPasses] = useState([]);
    const [selectedPass, setSelectedPass] = useState(null);
    const [error, setError] = useState(null);
    const [isPopupVisible, setIsPopupVisible] = useState(false);
    const [isCreating, setIsCreating] = useState(false);

    const [userPassPurchases, setUserPassPurchases] = useState([]);
    const [invoice, setInvoice] = useState(null);
    const [purchasesLoading, setPurchasesLoading] = useState(true);
    const [purchasesError, setPurchasesError] = useState(null);
    const [allPassPurchases, setAllPassPurchases] = useState([]);
    const [filterName, setFilterName] = useState("");

    const [allPassPurchasesPage, setAllPassPurchasesPage] = useState(1);
    const [allPassPurchasesPageSize, setAllPassPurchasesPageSize] = useState(10);

    const [userPassPurchasesPage, setUserPassPurchasesPage] = useState(1);
    const [userPassPurchasesPageSize, setUserPassPurchasesPageSize] = useState(10);

    // Fetch passes  
    const fetchPasses = useCallback(async () => {
        setLoading(true);
        try {
            const passesData = await getPasses();
            setPasses(passesData);
        } catch (err) {
            console.error("Error fetching passes", err);
            setError("Failed to load passes. Please try again later.");
        } finally {
            setLoading(false);
        }
    }, []); // Empty dependency array since getPasses doesn't depend on external variables  

    // Fetch all pass purchases (for admin)  
    const fetchAllPassPurchases = useCallback(async () => {
        setPurchasesLoading(true);
        try {
            const passPurchasesData = await getPassPurchases();
            if (Array.isArray(passPurchasesData)) {
                setAllPassPurchases(passPurchasesData);
            } else {
                throw new Error("Invalid data format");
            }
        } catch (err) {
            console.error("Error fetching all pass purchases", err);
            setPurchasesError("Failed to load pass purchases. Please try again later.");
        } finally {
            setPurchasesLoading(false);
        }
    }, []);

    // Fetch user's pass purchases (for non-admin)  
    const fetchMyPasses = useCallback(async () => {
        setPurchasesLoading(true);
        try {
            const contestants = await searchContestants();
            if (contestants.length === 0) {
                throw new Error("No contestants found");
            }

            const invoices = await getInvoicesByContestant(
                contestants[0]._id,
                settings.selectedRound
            );
            if (invoices.length === 0) {
                throw new Error("No invoices found for the contestant");
            }

            setInvoice(invoices[0]);
            setUserPassPurchases(invoices[0].passes || []);
        } catch (err) {
            console.error("Error fetching passes from invoice", err);
            setPurchasesError("Failed to load passes. Please try again later.");
        } finally {
            setPurchasesLoading(false);
        }
    }, [settings.selectedRound]);

    // Fetch data on initial render  
    useEffect(() => {
        const fetchData = async () => {
            await fetchPasses();
            if (userRole === "admin") {
                await fetchAllPassPurchases();
            } else {
                await fetchMyPasses();
            }
        };
        fetchData();
    }, [fetchPasses, fetchAllPassPurchases, fetchMyPasses, userRole]);

    // Add pass to invoice  
    const handleAddPassToInvoice = useCallback(
        async (passId, invoiceId, idNumber, name) => {
            setSubmitting(true);
            try {
                await addPassToInvoice(passId, invoiceId, idNumber, name);
                openNotificationWithIcon(
                    "success",
                    "Pass Added to Invoice",
                    "The pass has been successfully added to the invoice!"
                );

                // Refresh user's passes  
                await fetchMyPasses();
            } catch (err) {
                console.error("Error adding pass to invoice", err);
                openNotificationWithIcon(
                    "error",
                    "Add Pass to Invoice Failed",
                    `Failed to add pass to invoice. ${err.message}`
                );
            } finally {
                setSubmitting(false);
            }
        },
        [fetchMyPasses]
    );

    // Remove pass from invoice  
    const handleRemovePass = useCallback(
        async (passId) => {
            try {
                if (invoice) {
                    await removePassFromInvoice(invoice._id, passId);
                    setUserPassPurchases((prevPasses) =>
                        prevPasses.filter((p) => p.pass._id !== passId)
                    );
                    message.success("Pass removed successfully");
                } else {
                    message.error("No invoice found to remove the pass");
                }
            } catch (error) {
                console.error("Error removing pass from invoice:", error);
                message.error("Failed to remove pass from invoice");
            }
        },
        [invoice]
    );

    const confirmRemovePass = useCallback(
        (passId) => {
            Modal.confirm({
                title: "Are you sure you want to remove this pass?",
                content: "This action cannot be undone.",
                okText: "Yes",
                okType: "danger",
                cancelText: "No",
                onOk: () => handleRemovePass(passId),
            });
        },
        [handleRemovePass]
    );

    // Open pass form popup  
    const openPopup = useCallback((pass) => {
        setSelectedPass(pass);
        setIsPopupVisible(true);
        setIsCreating(!pass);
    }, []);

    const closePopup = useCallback(() => {
        setIsPopupVisible(false);
    }, []);

    // Handle pass creation/updation  
    const handleNewPassSubmit = useCallback(
        async (values) => {
            setSubmitting(true);
            try {
                if (userRole === "admin") {
                    if (!isCreating && selectedPass) {
                        await updatePass(selectedPass._id, values);
                        openNotificationWithIcon(
                            "success",
                            "Pass Updated",
                            "The pass has been successfully updated!"
                        );
                    } else {
                        await createPass(values);
                        openNotificationWithIcon(
                            "success",
                            "Pass Created",
                            "The pass has been successfully created!"
                        );
                    }
                    // Refresh passes  
                    await fetchPasses();
                } else {
                    const idNumber = values.idNumber;
                    const name = values.name;

                    // Get the contestant's invoice  
                    const contestants = await searchContestants();
                    if (contestants.length === 0) {
                        throw new Error("No contestants found");
                    }

                    const invoices = await getInvoicesByContestant(
                        contestants[0]._id,
                        settings.selectedRound
                    );
                    if (invoices.length === 0) {
                        throw new Error("No invoices found for the contestant");
                    }

                    const invoiceId = invoices[0]._id;

                    await handleAddPassToInvoice(selectedPass._id, invoiceId, idNumber, name);

                    // Update invoice state  
                    setInvoice(invoices[0]);
                }
                closePopup();
            } catch (err) {
                console.error("Error creating/updating pass", err);
                openNotificationWithIcon(
                    "error",
                    userRole === "admin" ? "Pass Creation Failed" : "Add Pass to Invoice Failed",
                    `Failed to ${userRole === "admin" ? "create/update pass" : "add pass to invoice"}. ${err.message
                    }`
                );
            } finally {
                setSubmitting(false);
            }
        },
        [
            userRole,
            isCreating,
            selectedPass,
            fetchPasses,
            handleAddPassToInvoice,
            settings.selectedRound,
            closePopup,
        ]
    );

    // Handle pass deletion (admin)  
    const handleDeletePass = useCallback(
        async (passId) => {
            setLoading(true);
            try {
                await deletePass(passId);
                openNotificationWithIcon(
                    "success",
                    "Pass Deleted",
                    "The pass has been successfully deleted!"
                );
                // Refresh passes  
                await fetchPasses();
            } catch (err) {
                console.error("Error deleting pass", err);
                openNotificationWithIcon(
                    "error",
                    "Pass Deletion Failed",
                    `Failed to delete pass. ${err.message}`
                );
            } finally {
                setLoading(false);
            }
        },
        [fetchPasses]
    );

    // Memoized filtering for all pass purchases  
    const filteredAllPassPurchases = useMemo(() => {
        return allPassPurchases.filter(
            (item) =>
                item.name && item.name.toLowerCase().includes(filterName.toLowerCase())
        );
    }, [allPassPurchases, filterName]);

    // Memoized pagination for all pass purchases  
    const allPassPurchasesToShow = useMemo(() => {
        const startIndex = (allPassPurchasesPage - 1) * allPassPurchasesPageSize;
        const endIndex = allPassPurchasesPage * allPassPurchasesPageSize;
        return filteredAllPassPurchases.slice(startIndex, endIndex);
    }, [
        filteredAllPassPurchases,
        allPassPurchasesPage,
        allPassPurchasesPageSize,
    ]);

    // Memoized filtering for user's pass purchases  
    const filteredUserPassPurchases = useMemo(() => {
        return userPassPurchases.filter(
            (item) =>
                item.name && item.name.toLowerCase().includes(filterName.toLowerCase())
        );
    }, [userPassPurchases, filterName]);

    // Memoized pagination for user's pass purchases  
    const userPassPurchasesToShow = useMemo(() => {
        const startIndex = (userPassPurchasesPage - 1) * userPassPurchasesPageSize;
        const endIndex = userPassPurchasesPage * userPassPurchasesPageSize;
        return filteredUserPassPurchases.slice(startIndex, endIndex);
    }, [
        filteredUserPassPurchases,
        userPassPurchasesPage,
        userPassPurchasesPageSize,
    ]);

    if (error) {
        return <div className="error-message">{error}</div>;
    }

    return (
        <div>
            <Flex
                style={{ marginBottom: "15px", height: "50px" }}
                align="center"
                justify="space-between"
            >
                <Title level={4} style={{ margin: 0 }}>
                    Passes
                </Title>
                {userRole === "admin" && (
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => openPopup(null)}
                    >
                        Create Pass
                    </Button>
                )}
            </Flex>
            <Card bordered={false}>
                <Tabs defaultActiveKey={userRole === "admin" ? "1" : "2"}>
                    {userRole === "admin" && (
                        <TabPane tab="All Pass Purchases" key="1">
                            <Space direction="vertical" size={"middle"} style={{ width: "100%" }}>
                                <Input
                                    placeholder="Filter by name"
                                    value={filterName}
                                    onChange={(e) => {
                                        setFilterName(e.target.value);
                                        setAllPassPurchasesPage(1);
                                    }}
                                />
                                {purchasesLoading ? (
                                    <Skeleton active />
                                ) : (
                                    <>
                                        <List
                                            bordered
                                            dataSource={allPassPurchasesToShow}
                                            renderItem={(item) => (
                                                <List.Item>
                                                    <Typography.Text>
                                                        {item.pass.type} ({item.pass.day})
                                                    </Typography.Text>
                                                    : {item.name} - R{item.price}
                                                </List.Item>
                                            )}
                                        />
                                        <Pagination
                                            current={allPassPurchasesPage}
                                            pageSize={allPassPurchasesPageSize}
                                            total={filteredAllPassPurchases.length}
                                            onChange={(page, pageSize) => {
                                                setAllPassPurchasesPage(page);
                                                setAllPassPurchasesPageSize(pageSize);
                                            }}
                                            showSizeChanger
                                        />
                                    </>
                                )}
                            </Space>
                        </TabPane>
                    )}
                    {userRole === "admin" ? (
                        <TabPane tab="Manage Passes" key="2">
                            <PassTable
                                passes={passes}
                                loading={loading}
                                openPopup={openPopup}
                                userRole={userRole}
                                handleDeletePass={handleDeletePass}
                            />
                        </TabPane>
                    ) : (
                        <>
                            <TabPane tab="Add Passes" key="2">
                                <PassTable
                                    passes={passes}
                                    loading={loading}
                                    openPopup={openPopup}
                                    userRole={userRole}
                                />
                            </TabPane>
                            <TabPane tab="My Pass Purchases" key="3">
                                {purchasesLoading ? (
                                    <Skeleton active />
                                ) : (
                                    <Space
                                        direction="vertical"
                                        size="middle"
                                        style={{ width: "100%" }}
                                    >
                                        <List
                                            bordered
                                            dataSource={userPassPurchasesToShow}
                                            renderItem={(item) => (
                                                <List.Item
                                                    actions={
                                                        invoice?.paid
                                                            ? []
                                                            : [
                                                                <Button
                                                                    type="link"
                                                                    icon={<CloseOutlined />}
                                                                    onClick={() =>
                                                                        confirmRemovePass(item.pass._id)
                                                                    }
                                                                />,
                                                            ]
                                                    }
                                                >
                                                    <Typography.Text>
                                                        {item.pass.type} ({item.pass.day})
                                                    </Typography.Text>
                                                    : {item.name} - R{item.pass.price}
                                                    {invoice?.paid ? (
                                                        <Tag color="green" style={{ marginLeft: 10 }}>
                                                            Paid
                                                        </Tag>
                                                    ) : (
                                                        <Tag color="red" style={{ marginLeft: 10 }}>
                                                            Unpaid
                                                        </Tag>
                                                    )}
                                                </List.Item>
                                            )}
                                        />
                                        <Pagination
                                            current={userPassPurchasesPage}
                                            pageSize={userPassPurchasesPageSize}
                                            total={filteredUserPassPurchases.length}
                                            onChange={(page, pageSize) => {
                                                setUserPassPurchasesPage(page);
                                                setUserPassPurchasesPageSize(pageSize);
                                            }}
                                            hideOnSinglePage
                                            showSizeChanger
                                        />
                                    </Space>
                                )}
                            </TabPane>
                        </>
                    )}
                </Tabs>
            </Card>
            {isCreating ? (
                <CreatePassForm
                    visible={isPopupVisible}
                    onCancel={closePopup}
                    onSubmit={handleNewPassSubmit}
                    userRole={userRole}
                    submitting={submitting}
                />
            ) : (
                <PassForm
                    visible={isPopupVisible}
                    onCancel={closePopup}
                    onSubmit={handleNewPassSubmit}
                    selectedPass={selectedPass}
                    userRole={userRole}
                    submitting={submitting}
                />
            )}
        </div>
    );
};

export default Passes;  
